import React from 'react';
import { Link } from 'react-router-dom';
import { useReadingContext } from '../../contexts/ReadingContext';
import { useFamilyContext } from '../../contexts/FamilyContext';
import { 
  BookOpen, 
  ChevronLeft, 
  MoreHorizontal, 
  Flame,
  Settings
} from 'lucide-react';
import QuickUpdateCard from './components/QuickUpdateCard';
import RewardsSection from './components/RewardsSection';
import ChallengeProgress from './components/ChallengeProgress';
import FamilyRecommendations from './components/FamilyRecommendations';
import AchievementDisplay from './components/AchievementDisplay';
import CelebrationBanner from './components/CelebrationBanner';
import BookSwitcherModal from './components/BookSwitcherModal';
import ReadingChallengeView from './components/ReadingChallengeView';
import ActiveReadingView from './components/ActiveReadingView';

export default function StellaReadingDashboard() {
  const { currentUser } = useFamilyContext();
  const {
    // Data
    currentBook,
    readingStats,
    rewards,
    activeChallenge,
    familyRecommendations,
    achievements,
    isLoading,
    error,
    isBookSwitcherOpen,
    // Actions
    updateProgress,
    completeBook,
    openBookSwitcher,
    closeBookSwitcher,
    loadReadingData,
    handleRecommendationAction,
    startNewChallenge,
    startNewBook
  } = useReadingContext();

  // Determine which view to show
  const shouldShowChallengeView = !activeChallenge && !isLoading;
  const shouldShowActiveView = activeChallenge && !isLoading;

  // Handle progress update
  const handleProgressUpdate = async (sessionId, newPage) => {
    try {
      await updateProgress(sessionId, newPage);
    } catch (error) {
      console.error('Error updating progress:', error);
    }
  };

  // Handle book completion
  const handleBookCompletion = async (sessionId) => {
    try {
      await completeBook(sessionId);
    } catch (error) {
      console.error('Error completing book:', error);
    }
  };

  // Handle book switching
  const handleBookSwitch = () => {
    openBookSwitcher();
  };

  // Handle challenge creation
  const handleStartChallenge = async (challengeData) => {
    try {
      await startNewChallenge(challengeData);
    } catch (error) {
      console.error('Error starting challenge:', error);
    }
  };

  // Handle starting a new book
  const handleStartNewBook = async (bookData) => {
    try {
      await startNewBook(bookData);
    } catch (error) {
      console.error('Error starting new book:', error);
    }
  };

  // Handle adding to Amazon (placeholder for now)
  const handleAddToAmazonList = (book) => {
    // This could open Amazon in a new tab or add to a wishlist
    const searchQuery = encodeURIComponent(`${book.book_title || book.title} ${book.book_author || book.author}`);
    const amazonUrl = `https://www.amazon.com/s?k=${searchQuery}&i=stripbooks`;
    window.open(amazonUrl, '_blank');
  };


  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-cream-50 to-amber-50 flex items-center justify-center">
        <div className="text-center">
          <BookOpen className="w-12 h-12 text-amber-500 animate-pulse mx-auto mb-4" />
          <p className="text-stone-600">Loading your reading challenge...</p>
        </div>
      </div>
    );
  }

  // Show challenge setup view if no active challenge
  if (shouldShowChallengeView) {
    return (
      <>
        <ReadingChallengeView
          books={familyRecommendations}
          onStartChallenge={handleStartChallenge}
          onSelectBook={handleStartNewBook}
          isLoading={isLoading}
        />
        
        {/* Error Display */}
        {error && (
          <div className="fixed bottom-4 right-4 bg-red-500 text-white px-4 py-2 rounded-lg shadow-lg">
            {error}
          </div>
        )}
      </>
    );
  }

  // Show active challenge view if there's an active challenge
  if (shouldShowActiveView) {
    return (
      <>
        <ActiveReadingView
          challenge={activeChallenge}
          currentBook={currentBook}
          readingStats={readingStats}
          familyRecommendations={familyRecommendations}
          onUpdateProgress={handleProgressUpdate}
          onCompleteBook={handleBookCompletion}
          onStartNewBook={handleStartNewBook}
          onOpenBookSwitcher={handleBookSwitch}
          onAddToAmazonList={handleAddToAmazonList}
          isLoading={isLoading}
        />

        {/* Book Switcher Modal */}
        {isBookSwitcherOpen && (
          <BookSwitcherModal
            isOpen={isBookSwitcherOpen}
            onClose={closeBookSwitcher}
            currentBook={currentBook}
          />
        )}

        {/* Error Display */}
        {error && (
          <div className="fixed bottom-4 right-4 bg-red-500 text-white px-4 py-2 rounded-lg shadow-lg">
            {error}
          </div>
        )}
      </>
    );
  }

  // Fallback to original dashboard view
  return (
    <div className="min-h-screen bg-gradient-to-br from-cream-50 to-amber-50">
      {/* Header */}
      <header className="bg-white border-b border-amber-200 sticky top-0 z-50 shadow-sm">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Link 
                to="/dashboard" 
                className="p-2 text-stone-600 hover:text-stone-900 hover:bg-stone-100 rounded-lg transition-colors"
              >
                <ChevronLeft className="w-5 h-5" />
              </Link>
              
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl flex items-center justify-center text-white shadow-lg">
                  <BookOpen className="w-5 h-5" />
                </div>
                <div>
                  <h1 className="text-lg font-bold text-slate-900">Reading Challenge</h1>
                  <p className="text-sm text-amber-600 font-medium flex items-center gap-1">
                    {readingStats.currentStreak}-day streak!
                    <Flame className="w-3.5 h-3.5" />
                  </p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              {currentUser?.isParent && (
                <Link 
                  to="/reading/admin" 
                  className="p-2 text-amber-600 hover:text-amber-700 hover:bg-amber-50 rounded-lg transition-colors"
                  title="Admin Panel"
                >
                  <Settings className="w-5 h-5" />
                </Link>
              )}
              <button className="p-2 text-stone-600 hover:bg-stone-100 rounded-lg transition-colors">
                <MoreHorizontal className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="px-4 py-4 space-y-5 pb-8">
        {/* Celebration Banner */}
        <CelebrationBanner stats={readingStats} />

        {/* Quick Update Card */}
        <QuickUpdateCard 
          currentBook={currentBook}
          onProgressUpdate={handleProgressUpdate}
          onBookCompletion={handleBookCompletion}
          onBookSwitch={handleBookSwitch}
        />

        {/* Rewards Section */}
        <RewardsSection rewards={rewards} />

        {/* Challenge Progress */}
        <ChallengeProgress challenge={activeChallenge} />

        {/* Family Recommendations */}
        <FamilyRecommendations 
          recommendations={familyRecommendations} 
          onRecommendationAction={handleRecommendationAction}
        />

        {/* Recent Achievements */}
        <AchievementDisplay achievements={achievements} />
      </main>

      {/* Book Switcher Modal */}
      {isBookSwitcherOpen && (
        <BookSwitcherModal
          isOpen={isBookSwitcherOpen}
          onClose={closeBookSwitcher}
          currentBook={currentBook}
        />
      )}

      {/* Error Display */}
      {error && (
        <div className="fixed bottom-4 right-4 bg-red-500 text-white px-4 py-2 rounded-lg shadow-lg">
          {error}
        </div>
      )}
    </div>
  );
}

